import { useEffect, useState } from "react";

import { Menu, MenuItem, MenuItemType } from "@/client";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import {
  DndContext,
  DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { useTranslation } from "react-i18next";

import { useUpdateMenuOrder } from "../hooks/use-update-menu-order";

import { AddNavigationItem } from "./add-navigation-item";
import { DeleteNavigation } from "./delete-navigation";
import { renderNavigationItemForm } from "./render-navigation-item-form";
import { SortableNavigationItem } from "./sortable-navigation-item";

type NavigationProps = {
  data: Menu;
};

function Navigation(props: NavigationProps) {
  const { t } = useTranslation();
  const { data } = props;

  const [newMenuItem, setNewMenuItem] = useState<MenuItemType | null>(null);
  const [openedItems, setOpenedItems] = useState<string[]>([]);
  const [itemsOrder, setItemsOrder] = useState<string[]>([]);

  const menuItemsMap = new Map(data.items.map((obj) => [obj.id, obj]));
  const orderedMenuItems: MenuItem[] = itemsOrder
    .map((id) => menuItemsMap.get(id))
    .filter((obj): obj is MenuItem => obj !== undefined);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  useEffect(() => {
    setItemsOrder(data.items.map((item) => item.id));
  }, [setItemsOrder, data.items]);

  const { mutateAsync: updateOrder } = useUpdateMenuOrder();

  async function handleDragEnd(event: DragEndEvent) {
    const { active, over } = event;

    if (active.id !== over?.id) {
      const oldIndex = itemsOrder.indexOf(active.id.toString());
      const newIndex = itemsOrder.indexOf(over ? over.id.toString() : "");

      if (oldIndex === -1 || newIndex === -1) {
        console.error("Could not find item indices for drag and drop");
        return;
      }

      // Optimistically update the UI
      const newItemsOrder = arrayMove(itemsOrder, oldIndex, newIndex);
      setItemsOrder(newItemsOrder);

      try {
        await updateOrder({
          menuId: data.id,
          requestBody: {
            oldIndex,
            newIndex,
          },
        });
      } catch (error) {
        // Revert on error
        setItemsOrder(itemsOrder);
        console.error("Failed to update menu order:", error);
      }
    }
  }

  const handleAccordionClose = (menuItemId: string) => {
    setOpenedItems((prev) =>
      prev.filter((accordionItem) => accordionItem !== menuItemId),
    );
  };

  return (
    <div>
      <p className="mb-4 flex items-center text-lg font-semibold">
        {data.title}
        {data.isSidebar && (
          <span className="ml-1 text-sm font-thin">{t("navigationPage.sidebar")}</span>
        )}
        <AddNavigationItem
          onSelect={(e) => {
            setOpenedItems([`new-menu-item-${data.id}`]);
            setNewMenuItem(e);
          }}
        />
        <DeleteNavigation navigationId={data.id} />
      </p>

      <Accordion
        type="multiple"
        className="w-full space-y-2"
        value={openedItems}
        onValueChange={setOpenedItems}
      >
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          <SortableContext
            items={orderedMenuItems.map((item) => item.id)}
            strategy={verticalListSortingStrategy}
          >
            {orderedMenuItems.map((item) => (
              <SortableNavigationItem
                key={item.id}
                item={item}
                menuId={data.id}
                disabled={!!newMenuItem}
                onClose={() => handleAccordionClose(item.id)}
              />
            ))}
          </SortableContext>
        </DndContext>

        {newMenuItem && (
          <AccordionItem value={`new-menu-item-${data.id}`} disabled>
            <AccordionTrigger>
              <span className="material-icons mr-1.5 text-foreground">add</span>
              {t("navigationPage.draftMenuItem")}
              <span className="ml-auto mr-2 text-sm lowercase text-muted-foreground">
                {newMenuItem.replaceAll("_", " ")}
              </span>
            </AccordionTrigger>
            <AccordionContent>
              {renderNavigationItemForm({
                menuId: data.id,
                menuItemType: newMenuItem,
                onClose: () => setNewMenuItem(null),
              })}
            </AccordionContent>
          </AccordionItem>
        )}
      </Accordion>
    </div>
  );
}

export { Navigation };
